/**
 * PO Sections Fields Component
 *
 * Renders the unstructured sections of the Purchase Order form.
 * Sections include Terms & Conditions, Special Instructions, Notes, etc.
 *
 * Uses react-hook-form's useFieldArray for dynamic array management.
 * Each section can contain either a list of items (with labels) or raw text.
 */

import { useFieldArray } from 'react-hook-form';
import Input from '../common/Input';
import Button from '../common/Button';
import { normalizeConfidence, getFieldScore, getFieldIssues, unwrapEvaluation } from '../../utils/confidenceUtils';

/**
 * POSectionsFields Component
 *
 * @param {Object} props - Component props
 * @param {Object} props.form - React Hook Form instance
 * @param {Object} props.confidence - Confidence scores from AI extraction
 */
function POSectionsFields({ form, evaluation: rawEvaluation, confidence = {}, issues = [] }) {
  const evaluation = unwrapEvaluation(rawEvaluation);
  const { register, control, formState: { errors } } = form;

  // useFieldArray manages the dynamic sections array
  const { fields: sections, append, remove, move } = useFieldArray({
    control,
    name: 'sections'
  });

  /**
   * Add a new empty section
   */
  const addSection = () => {
    append({
      type: 'other',
      title: '',
      items: [{ text: '', label: '' }],
      rawText: '',
      order: sections.length + 1
    });
  };

  // Check evaluation or legacy confidence for sections score.
  // Try evaluation field_scores for any sections paths first,
  // then fall back to the legacy scalar confidence.sections value.
  const getSectionsScore = () => {
    if (evaluation?.field_scores) {
      let min = 1;
      let hasRelevant = false;
      for (const [path, fieldData] of Object.entries(evaluation.field_scores)) {
        if (!path.startsWith('sections')) continue;
        const score = typeof fieldData === 'object' ? fieldData.score : fieldData;
        if (score == null) continue;
        hasRelevant = true;
        if (score < min) min = score;
      }
      if (hasRelevant) return min;
    }
    // Fall back to legacy confidence.sections
    const rawSectionsConf = confidence.sections;
    if (rawSectionsConf === undefined) return undefined;
    return normalizeConfidence(rawSectionsConf, sections.length === 0);
  };
  const sectionsConf = getSectionsScore();
  const hasSectionsConfidence = sectionsConf !== undefined;
  const hasLowConfidence = hasSectionsConfidence && sectionsConf < 0.8;

  return (
    <div className="space-y-4">
      {/* Section Title with confidence indicator */}
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg font-medium text-gray-900">
          Document Sections
          {hasSectionsConfidence && (
            <span className={`ml-2 text-sm font-normal ${hasLowConfidence ? 'text-yellow-600' : 'text-green-600'}`}>
              ({Math.round(sectionsConf * 100)}%)
            </span>
          )}
        </h3>
        <Button variant="secondary" size="sm" onClick={addSection}>
          + Add Section
        </Button>
      </div>

      {/* Empty state */}
      {sections.length === 0 && (
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
          No additional sections found. Click "Add Section" to add terms, notes, or instructions.
        </div>
      )}

      {/* Section entries */}
      {sections.map((section, sectionIndex) => (
        <SectionCard
          key={section.id}
          section={section}
          index={sectionIndex}
          form={form}
          issues={issues}
          onRemove={() => remove(sectionIndex)}
          onMoveUp={() => sectionIndex > 0 && move(sectionIndex, sectionIndex - 1)}
          onMoveDown={() => sectionIndex < sections.length - 1 && move(sectionIndex, sectionIndex + 1)}
          hasLowConfidence={hasLowConfidence}
          isFirst={sectionIndex === 0}
          isLast={sectionIndex === sections.length - 1}
        />
      ))}
    </div>
  );
}

/**
 * SectionCard Component
 * Renders a single section with its items
 */
function SectionCard({ section, index, form, issues = [], onRemove, onMoveUp, onMoveDown, hasLowConfidence, isFirst, isLast }) {
  const { register, control } = form;

  // Nested useFieldArray for section items
  const { fields: items, append: appendItem, remove: removeItem } = useFieldArray({
    control,
    name: `sections.${index}.items`
  });

  const addItem = () => {
    appendItem({ text: '', label: '' });
  };

  return (
    <div
      className={`
        border rounded-lg p-4 space-y-3
        ${hasLowConfidence ? 'border-amber-400 bg-amber-50' : 'border-gray-200'}
      `}
    >
      {/* Section header */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-gray-700">
          Section #{index + 1}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onMoveUp}
            disabled={isFirst}
            className={`text-gray-400 hover:text-gray-600 ${isFirst ? 'opacity-30 cursor-not-allowed' : ''}`}
            title="Move up"
          >
            ↑
          </button>
          <button
            type="button"
            onClick={onMoveDown}
            disabled={isLast}
            className={`text-gray-400 hover:text-gray-600 ${isLast ? 'opacity-30 cursor-not-allowed' : ''}`}
            title="Move down"
          >
            ↓
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="text-red-500 hover:text-red-700 text-sm"
          >
            Remove
          </button>
        </div>
      </div>

      {/* Section type and title */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Input
          label="Type (normalized)"
          {...register(`sections.${index}.type`)}
          defaultValue={section.type}
          placeholder="terms_and_conditions"
          className="font-mono text-sm"
          issues={getFieldIssues(issues, `sections[${index}].type`)}
        />
        <Input
          label="Title (as in document)"
          {...register(`sections.${index}.title`)}
          defaultValue={section.title}
          placeholder="TERMS AND CONDITIONS:"
          issues={getFieldIssues(issues, `sections[${index}].title`)}
        />
      </div>

      {/* Section items */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">Items</label>
          <button
            type="button"
            onClick={addItem}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            + Add Item
          </button>
        </div>

        {items.length === 0 && (
          <div className="text-sm text-gray-500 italic">
            No items. Add items for lists, or use Raw Text below for paragraphs.
          </div>
        )}

        {items.map((item, itemIndex) => (
          <div key={item.id} className="flex gap-2 items-start">
            <input
              {...register(`sections.${index}.items.${itemIndex}.label`)}
              defaultValue={item.label}
              placeholder="1."
              className="w-16 px-2 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <input
              {...register(`sections.${index}.items.${itemIndex}.text`)}
              defaultValue={item.text}
              placeholder="Item text..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <button
              type="button"
              onClick={() => removeItem(itemIndex)}
              className="text-red-500 hover:text-red-700 px-2 py-2"
              title="Remove item"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Raw text (for non-list content) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Raw Text (for paragraphs)
        </label>
        <textarea
          {...register(`sections.${index}.rawText`)}
          defaultValue={section.rawText}
          placeholder="Use this for paragraph text instead of list items..."
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Hidden order field */}
      <input
        type="hidden"
        {...register(`sections.${index}.order`)}
        defaultValue={section.order || index + 1}
      />
    </div>
  );
}

export default POSectionsFields;
